import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { setLogout, generateOTP, verifyOTP } from '../../redux/actions';
import { PATH, getRoute } from '../../utils/routes';
import DesktopView from './components/DesktopView';
import MobileView from './components/MobileView';
import { useRouter } from 'next/router';
import useWindowDimensions from '../../utils/getWindowDimension';
import { toastNotifyError } from '../../components/Common/Toast';
import { MAX_MOBILE_WIDTH } from '../../utils/constants';
import LocalStorageHelper from '../../utils/LocalStorageHelper';
import {
  emailRegex,
  alphaNumericLettersRegex,
  alphabetsAndSpaceRegex,
  addressRegex,
} from '../../utils/regex';

const MemberLogin = ({ storeInfo }) => {
  const router = useRouter();
  const dispatch = useDispatch();
  const { width } = useWindowDimensions();
  let timerID = null;

  const [state, setState] = useState({
    phone: '',
    OTP: '',
    OTPsent: false,
    timer: 59,
    isTimeUp: false,
    invalidOtp: false,
    currentDeviceWidth: 0,
    memberOnlyAccessError: false,
  });

  useEffect(() => {
    if (!storeInfo?.services?.members_only_access)
      router.push(getRoute(PATH.PRODUCT, storeInfo?.store_info?.domain));
    else {
      dispatch(setLogout());
      LocalStorageHelper.delete('auth_token');
    }
  }, []);

  useEffect(() => {
    setState(state => ({ ...state, currentDeviceWidth: width }));
  }, [width]);

  useEffect(() => {
    if (state.OTPsent) {
      startTimer();
    }

    return () => {
      /** Clear the Inteeval if any */
      if (timerID != null) {
        clearInterval(timerID);
      }
    };
  }, [state.OTPsent]);

  const verifyOTPCallback = status => {
    if (status) {
      LocalStorageHelper.add(
        `isMemberAllowed&store_id=${storeInfo?.store_id}`,
        true
      );
      router.push(getRoute(PATH.PRODUCT, storeInfo.store_info.domain));
    } else {
      setState(state => ({ ...state, invalidOtp: true }));
    }
  };

  const validateInput = validationType => {
    switch (validationType) {
      case 'email': {
        return emailRegex();
      }
      case 'alpha_numeric': {
        return alphaNumericLettersRegex();
      }
      case 'alpha_space': {
        return alphabetsAndSpaceRegex();
      }
      case 'address': {
        return addressRegex();
      }
      default: {
        return /[^\d]/gi;
      }
    }
  };

  const handleInputChange = (
    key,
    value,
    validationType,
    minLength,
    maxLength
  ) => {
    if (value.length > maxLength) value = value.substring(0, maxLength);
    value = value.replace(validateInput(validationType), '');
    if (!isNaN(value) && key === 'phone')
      setState(state => ({
        ...state,
        phone: value,
        memberOnlyAccessError: false,
      }));
    else if (!isNaN(value) && key === 'otp') {
      setState(state => ({ ...state, OTP: value }));
      if (value.length < 4) {
        setState(state => ({ ...state, invalidOtp: false }));
      }
    }
  };

  const setPhoneNumber = () => {
    const phoneReg = /(6|7|8|9)\d{9}/;
    if (
      state.phone &&
      state.phone.length === 10 &&
      state.phone.match(phoneReg)
    ) {
      const queryParams = storeInfo?.services?.members_only_access
        ? `&store_id=${storeInfo?.store_id}`
        : '';
      const callback = status => {
        if (status) setState(state => ({ ...state, OTPsent: true }));
        else
          setState(prevState => ({
            ...prevState,
            memberOnlyAccessError: true,
          }));
      };
      dispatch(generateOTP(state.phone, router, 'web', queryParams, callback));
    } else {
      toastNotifyError('Invalid Phone Number!');
    }
  };

  // Start the timmer
  const startTimer = () => {
    let seconds = state.timer;
    timerID = setInterval(() => tick(--seconds), 1000);
  };

  // Increase the timing (second) tick
  const tick = seconds => {
    let updatedTime;
    if (seconds > 0) {
      updatedTime = (seconds < 10 ? '0' : '') + String(seconds);
      setState(state => ({
        ...state,
        timer: updatedTime,
      }));
    } else {
      clearInterval(timerID);
      setState(state => ({
        ...state,
        timer: 59,
        isTimeUp: true,
      }));
    }
  };

  const resendOTP = () => {
    setState(state => ({ ...state, isTimeUp: false }));
    dispatch(generateOTP(state.phone));
    let seconds = state.timer;
    timerID = setInterval(() => tick(--seconds), 1000);
  };

  const handleChange = val => {
    setState(state => ({ ...state, OTP: val }));
  };

  const submitVerifyOTP = () => {
    const data = {
      phone: state.phone,
      otp: state.OTP,
    };
    if (state.OTP.length === 4) {
      dispatch(verifyOTP(data, router, false, verifyOTPCallback));
    } else {
      toastNotifyError('Please enter valid OTP');
    }
  };

  const toggleOTPsent = () => {
    setState(state => ({
      ...state,
      OTPsent: !state.OTPsent,
      phone: '',
      timer: 59,
      isTimeUp: false,
      invalidOtp: false,
    }));
  };

  const Props = {
    OTP: state.OTP,
    OTPsent: state.OTPsent,
    phone: state.phone,
    timer: state.timer,
    isTimeUp: state.isTimeUp,
    memberOnlyAccessError: state.memberOnlyAccessError,
    storeInfo: storeInfo,
    invalidOtp: state.invalidOtp,
    setPhoneNumber,
    handleInputChange,
    resendOTP,
    handleChange,
    submitVerifyOTP,
    toggleOTPsent,
  };

  return state.currentDeviceWidth <= MAX_MOBILE_WIDTH ? (
    <MobileView {...Props} />
  ) : (
    <DesktopView {...Props} />
  );
};

export default MemberLogin;
